import ApartmentIcon from '@mui/icons-material/Apartment'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PersonIcon from '@mui/icons-material/Person'
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import Box from '@mui/material/Box'
import I18n from 'i18n-js'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import { deleteOmniauthCookies } from '@src/components/auth/omniauthHelpers'
import manageBrandsPaths from '@src/components/brands/manageBrandsPaths'
import retailerSettingsPaths from '@src/components/retailerSettings/retailerSettingsPaths'
import AppConfig from '@src/config/AppConfig'
import LinkChild from '@src/lib/LinkChild'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { createR1UrlBasePath } from '@src/lib/Utils'
import { selectShowBrandDashboardLink } from '@src/stores/mainMenu/selectors'

import useTracking from '../Tracking/useTracking'

import LanguageSelector from './LanguageSelector'

const useStyles = makeStyles()((theme) => ({
  manageBrandsItem: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}))

interface AccountDropdownProps {
  currentSlugs: {
    retailerAccount: string
    brand: string
  }
  text?: string
}

const AccountDropdown: React.FC<
  React.PropsWithChildren<AccountDropdownProps>
> = (props) => {
  const { classes } = useStyles()
  const { showBrandDashboardLink } = useSelector(selectShowBrandDashboardLink)

  const { track } = useTracking()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>()
  const open = Boolean(anchorEl)
  const toggleMenu = (event?: React.MouseEvent<HTMLElement>) => {
    const element = event?.currentTarget
    setAnchorEl(element)
    track({
      event: 'Click',
      data: {
        type: 'Accounts Menu Toggle',
        showMenu: !!element,
      },
    })
  }
  const useNewBusinessSettings = useFeatureFlag('new-business-settings')

  function buildUrl(path: string) {
    return `/#${
      createR1UrlBasePath(
        props.currentSlugs.retailerAccount,
        props.currentSlugs.brand,
      ) + path
    }`
  }

  const businessInfoMenuItem = (
    <MenuItem
      component="a"
      href={
        useNewBusinessSettings
          ? // The link comes from LinkChild when the feature flag is on.
            undefined
          : buildUrl('settings')
      }
    >
      <ListItemIcon>
        <LocationOnIcon />
      </ListItemIcon>
      <Typography>{I18n.t('nav.businessInfo')}</Typography>
    </MenuItem>
  )

  return (
    <>
      <IconButton onClick={toggleMenu} color="inherit" size="large">
        <PersonIcon />
      </IconButton>
      <Menu
        id={'account-menu'}
        open={open}
        anchorEl={anchorEl}
        onClose={() => toggleMenu()}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {useNewBusinessSettings ? (
          <LinkChild
            to={retailerSettingsPaths.businessInfo.build({
              retailerAccountSlug: props.currentSlugs.retailerAccount,
            })}
          >
            {businessInfoMenuItem}
          </LinkChild>
        ) : (
          businessInfoMenuItem
        )}
        <MenuItem component="a" href="/#/account/profile">
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <Typography>{I18n.t('nav.userProfile')}</Typography>
        </MenuItem>
        <MenuItem
          className={classes.manageBrandsItem}
          component={Link}
          to={manageBrandsPaths.index.build({
            retailerAccountSlug: props.currentSlugs.retailerAccount,
            brandSlug: props.currentSlugs.brand,
          })}
        >
          <ListItemIcon>
            <ApartmentIcon />
          </ListItemIcon>
          <Typography>{I18n.t('manageBrands.manageBrands')}</Typography>
        </MenuItem>
        {showBrandDashboardLink && AppConfig && AppConfig.CP_BASE ? (
          <MenuItem component="a" href={AppConfig.CP_BASE}>
            <ListItemIcon>
              <AssessmentIcon />
            </ListItemIcon>
            <Typography>{I18n.t('shared.visitBrandDashboard')}</Typography>
          </MenuItem>
        ) : undefined}
        <LinkChild
          to="/login"
          onClick={() => {
            deleteOmniauthCookies()
          }}
        >
          <MenuItem divider component="a">
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <Typography>{I18n.t('shared.logout')}</Typography>
          </MenuItem>
        </LinkChild>

        <Box padding={2}>
          <LanguageSelector />
        </Box>
      </Menu>
    </>
  )
}

export default AccountDropdown
